import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import Layout from "@/components/Layout";
import client from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { RiskBadge } from "@/components/StatusBadge";
import { timeAgo, dueLabel, isToday, suggestedUpdate, closeoutBlocked } from "@/lib/format";
import {
  ListChecks, Clock, MessageSquare, FileText, ShieldAlert, CheckCircle2, Briefcase,
  ArrowRight, ChevronRight,
} from "lucide-react";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";

const isMissingDocs = (j) => (j.documents || []).some((d) => d.status === "Missing");
const isUpdateDue = (j) => (j.issues || []).includes("Customer update overdue") || isToday(j.customer_update_due_date);
const isCloseoutBlocked = (j) => closeoutBlocked(j);
const isReadyFinal = (j) =>
  (j.current_stage === "Ready to Close" && j.can_close) ||
  (j.current_stage === "Delivered" && j.closeout_eligible && (j.rtc_blockers || []).length === 0);

const SECTIONS = [
  { key: "overdue", title: "Overdue Next Actions", icon: Clock, tone: "#3F566E", test: (j) => j.next_action_state === "Overdue" },
  { key: "updates", title: "Customer Updates Due", icon: MessageSquare, tone: "#3F566E", test: isUpdateDue },
  { key: "docs", title: "Missing Documents", icon: FileText, tone: "#3F566E", test: isMissingDocs },
  { key: "blocked", title: "Closeout Blocked", icon: ShieldAlert, tone: "#3F566E", test: isCloseoutBlocked },
  { key: "ready", title: "Ready for Final Close", icon: CheckCircle2, tone: "#3F566E", test: isReadyFinal },
  { key: "all", title: "Assigned Active Jobs", icon: Briefcase, tone: "#516171", test: () => true },
];

const CHIPS = [
  { key: "all", label: "My assigned jobs" },
  { key: "today", label: "Due today" },
  { key: "overdue", label: "Overdue" },
  { key: "attention", label: "Needs Attention" },
  { key: "ready", label: "Ready to Close" },
];

function MetricCard({ label, value, tone, active, onClick }) {
  return (
    <button data-testid={`queue-metric-${label.toLowerCase().replace(/\s+/g, "-")}`} onClick={onClick}
      className={`text-left bg-surface border rounded-sm px-3.5 py-3 shadow-industrialCard transition-shadow hover:shadow-industrialHover focus:outline-none focus-visible:ring-2 focus-visible:ring-warn ${active ? "border-warn ring-1 ring-warn/30" : "border-line-strong"}`}>
      <div className="font-mono-data text-[22px] font-semibold leading-none" style={{ color: value > 0 ? tone : "#A6AEB6" }}>{value}</div>
      <div className="op-label mt-1.5">{label}</div>
    </button>
  );
}

export default function MyQueue() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const canTeam = ["admin", "manager", "super_admin"].includes(user?.role);
  const [jobs, setJobs] = useState([]);
  const [users, setUsers] = useState([]);
  const [templates, setTemplates] = useState({});
  const [person, setPerson] = useState(user?.name || "");
  const [chip, setChip] = useState("all");
  const [customer, setCustomer] = useState("all");
  const [stage, setStage] = useState("all");

  const load = () => client.get("/shipments").then((r) => setJobs(r.data));
  useEffect(() => {
    load();
    if (canTeam) client.get("/users").then((r) => setUsers(r.data)).catch(() => {});
    client.get("/meta").then((r) => setTemplates(r.data.update_templates || {})).catch(() => {});
  }, [canTeam]);

  useEffect(() => { if (user?.name && !person) setPerson(user.name); }, [user, person]);

  const base = useMemo(
    () => jobs.filter((j) => j.assigned_owner === person && j.current_stage !== "Closed"),
    [jobs, person]
  );

  const filtered = useMemo(() => {
    let list = base;
    if (chip === "today") list = list.filter((j) => isToday(j.next_action_due_date) || isToday(j.customer_update_due_date));
    if (chip === "overdue") list = list.filter((j) => j.next_action_state === "Overdue" || (j.issues || []).includes("Customer update overdue"));
    if (chip === "attention") list = list.filter((j) => j.needs_attention);
    if (chip === "ready") list = list.filter((j) => j.current_stage === "Ready to Close");
    if (customer !== "all") list = list.filter((j) => j.customer_name === customer);
    if (stage !== "all") list = list.filter((j) => j.current_stage === stage);
    return list;
  }, [base, chip, customer, stage]);

  const customers = useMemo(() => [...new Set(base.map((j) => j.customer_name))].sort(), [base]);
  const stages = useMemo(() => [...new Set(base.map((j) => j.current_stage))], [base]);

  // Owner options = workspace users + any owner actually assigned to a job (incl.
  // newly created jobs with custom owners) + the current user.
  const ownerOptions = useMemo(() => {
    const set = new Set();
    if (user?.name) set.add(user.name);
    users.forEach((u) => u.name && set.add(u.name));
    jobs.forEach((j) => j.assigned_owner && set.add(j.assigned_owner));
    return [...set].sort();
  }, [users, jobs, user]);

  const counts = {
    assigned: filtered.length,
    overdue: filtered.filter((j) => j.next_action_state === "Overdue").length,
    updates: filtered.filter(isUpdateDue).length,
    docs: filtered.filter(isMissingDocs).length,
    blocked: filtered.filter(isCloseoutBlocked).length,
    ready: filtered.filter(isReadyFinal).length,
  };

  const act = async (fn, msg) => {
    try { await fn(); await load(); if (msg) toast.success(msg); }
    catch (e) { toast.error(e.response?.data?.detail || "Action failed."); }
  };

  const templateFor = (j) => (j.risk_status === "Delayed" ? templates["Delayed"] : templates[j.current_stage]) || suggestedUpdate(j.current_stage, j.risk_status);

  const markDocsReceived = async (j) => {
    for (const d of (j.documents || []).filter((x) => x.status === "Missing")) {
      await client.put(`/shipments/${j.id}/documents/${encodeURIComponent(d.type)}`, { status: "Received" });
    }
  };

  // Returns {label, fn} for the section's quick action on a job, or null
  const quickAction = (key, j) => {
    if (key === "overdue") return { label: "Mark Next Action Complete", fn: () => act(() => client.put(`/shipments/${j.id}`, { next_action_status: "Completed" }), "Next action complete") };
    if (key === "updates") return { label: "Mark Update Sent", fn: () => act(() => client.post(`/shipments/${j.id}/customer-updates`, { message: templateFor(j) }), "Customer update sent") };
    if (key === "docs") return { label: "Mark Documents Received", fn: () => act(() => markDocsReceived(j), "Documents received") };
    if (key === "ready") {
      if (j.current_stage === "Ready to Close" && j.can_close)
        return { label: "Mark as Closed", fn: () => act(() => client.post(`/shipments/${j.id}/close`, {}), "Job closed.") };
      if (j.current_stage === "Delivered")
        return { label: "Move to Ready to Close", fn: () => act(() => client.put(`/shipments/${j.id}`, { current_stage: "Ready to Close" }), "Moved to Ready to Close") };
    }
    if (key === "blocked") {
      // Use the first actionable blocker
      if (j.current_stage === "Ready to Close") {
        const b = (j.close_blockers || []).find((x) => ["toggle", "mark_pod", "clear_risk"].includes(x.action));
        if (b?.action === "toggle") return { label: `Complete: ${b.name}`, fn: () => act(() => client.put(`/shipments/${j.id}/closeout-checklist`, { item: b.item, value: true }), "Updated") };
        if (b?.action === "mark_pod") return { label: "Mark POD Received", fn: () => act(async () => { await client.put(`/shipments/${j.id}/documents/Proof%20of%20Delivery`, { status: "Received" }); await client.put(`/shipments/${j.id}/closeout-checklist`, { item: "proof_of_delivery_received", value: true }); }, "POD received") };
        if (b?.action === "clear_risk") return { label: "Clear Risk Status", fn: () => act(() => client.put(`/shipments/${j.id}`, { risk_status: "On Track" }), "Risk cleared") };
      } else {
        const codes = (j.rtc_blockers || []).map((x) => x.code);
        if (codes.includes("pod_missing")) return { label: "Mark POD Received", fn: () => act(() => client.put(`/shipments/${j.id}/documents/Proof%20of%20Delivery`, { status: "Received" }), "POD received") };
        if (codes.includes("missing_doc")) return { label: "Mark Documents Received", fn: () => act(() => markDocsReceived(j), "Documents received") };
        if (codes.includes("risk")) return { label: "Clear Risk Status", fn: () => act(() => client.put(`/shipments/${j.id}`, { risk_status: "On Track" }), "Risk cleared") };
        if (codes.includes("next_action_overdue")) return { label: "Mark Next Action Complete", fn: () => act(() => client.put(`/shipments/${j.id}`, { next_action_status: "Completed" }), "Next action complete") };
      }
    }
    return null;
  };

  const issueText = (key, j) => {
    if (key === "blocked") {
      const list = j.current_stage === "Ready to Close" ? (j.close_blockers || []) : (j.rtc_blockers || []);
      return list.map((b) => b.name || b.message).slice(0, 2).join(" · ") || "Closeout blocked";
    }
    if (key === "docs") return "Missing: " + (j.documents || []).filter((d) => d.status === "Missing").map((d) => d.type).slice(0, 2).join(", ");
    if (key === "updates") return (j.issues || []).includes("Customer update overdue") ? "Customer update overdue" : "Update due today";
    if (key === "overdue") return "Next action overdue";
    if (key === "ready") return "Ready for final close";
    return (j.issues || [])[0] || "On track";
  };

  const QueueRow = ({ j, sectionKey }) => {
    const qa = quickAction(sectionKey, j);
    const open = () => navigate(`/app/shipments/${j.id}`);
    return (
      <div
        data-testid={`queue-row-${sectionKey}-${j.job_number}`}
        role="button"
        tabIndex={0}
        onClick={open}
        onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); open(); } }}
        className="border-b border-line last:border-0 px-4 py-3 hover:bg-surface-alt transition-colors cursor-pointer group focus:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-steel/40"
      >
        <div className="flex flex-wrap items-center gap-x-4 gap-y-2">
          <div className="flex items-center gap-2.5 min-w-0 flex-1">
            <span className="font-mono-data text-[12.5px] text-ink font-medium whitespace-nowrap">{j.job_number}</span>
            <span className="text-[13.5px] text-ink truncate">{j.customer_name}</span>
            <span className="text-[11.5px] text-ink-3 whitespace-nowrap hidden sm:inline">{j.current_stage}</span>
            <ChevronRight className="h-3.5 w-3.5 text-ink-3 opacity-0 group-hover:opacity-100 transition-opacity shrink-0" />
          </div>
          <div className="flex items-center gap-3 shrink-0">
            <span className="text-[12px] font-medium whitespace-nowrap" style={{ color: sectionKey === "ready" ? "#059669" : "#D97706" }}>{issueText(sectionKey, j)}</span>
            <RiskBadge status={j.risk_status} />
            <span className="font-mono-data text-[11.5px] text-ink-3 whitespace-nowrap w-[78px] text-right">
              {j.next_action_due_date ? dueLabel(j.next_action_due_date) : timeAgo(j.last_updated_at)}
            </span>
            {qa ? (
              <button data-testid={`queue-action-${sectionKey}-${j.job_number}`} onClick={(e) => { e.stopPropagation(); qa.fn(); }}
                className="h-7 px-2.5 rounded-sm bg-brand text-white text-[11.5px] font-medium hover:bg-brand-dark transition-colors whitespace-nowrap">{qa.label}</button>
            ) : (
              <button data-testid={`queue-open-${sectionKey}-${j.job_number}`} onClick={(e) => { e.stopPropagation(); open(); }}
                className="h-7 px-2.5 rounded-sm border border-line bg-surface text-[11.5px] font-medium text-ink-2 hover:text-ink hover:border-steel transition-colors flex items-center gap-1 whitespace-nowrap">Open <ArrowRight className="h-3 w-3" /></button>
            )}
          </div>
        </div>
        {j.next_action && (sectionKey === "overdue" || sectionKey === "all") && (
          <p className="text-[11.5px] text-ink-2 mt-1.5 line-clamp-1">{j.next_action}</p>
        )}
      </div>
    );
  };

  return (
    <Layout>
      <div className="flex flex-wrap items-start justify-between gap-3 mb-5">
        <div>
          <p className="op-label mb-1">Daily worklist</p>
          <h1 className="font-heading text-2xl font-semibold text-ink">My Queue</h1>
          <p className="text-sm text-ink-2 mt-1">Everything assigned to {person === user?.name ? "you" : person}, grouped by what needs action.</p>
        </div>
        {canTeam && (
          <div className="min-w-[180px]">
            <label className="op-label block mb-1">Viewing queue for</label>
            <Select value={person} onValueChange={setPerson}>
              <SelectTrigger data-testid="queue-person-select" className="h-9 border-line rounded-sm text-[13px]"><SelectValue /></SelectTrigger>
              <SelectContent>
                {ownerOptions.map((n) => (
                  <SelectItem key={n} value={n}>{n === user?.name ? `${n} (me)` : n}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        )}
      </div>

      {/* Metric cards */}
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-2.5 mb-5">
        <MetricCard label="Assigned Jobs" value={counts.assigned} tone="#0B132B" active={chip === "all" && customer === "all" && stage === "all"} onClick={() => { setChip("all"); setCustomer("all"); setStage("all"); }} />
        <MetricCard label="Overdue" value={counts.overdue} tone="#DC2626" active={chip === "overdue"} onClick={() => setChip(chip === "overdue" ? "all" : "overdue")} />
        <MetricCard label="Updates Due" value={counts.updates} tone="#D97706" active={false} onClick={() => { const el = document.querySelector('[data-section="updates"]'); el?.scrollIntoView({ behavior: "smooth", block: "start" }); }} />
        <MetricCard label="Missing Documents" value={counts.docs} tone="#DC2626" active={false} onClick={() => { const el = document.querySelector('[data-section="docs"]'); el?.scrollIntoView({ behavior: "smooth", block: "start" }); }} />
        <MetricCard label="Closeout Blocked" value={counts.blocked} tone="#DC2626" active={false} onClick={() => { const el = document.querySelector('[data-section="blocked"]'); el?.scrollIntoView({ behavior: "smooth", block: "start" }); }} />
        <MetricCard label="Ready for Final Close" value={counts.ready} tone="#059669" active={chip === "ready"} onClick={() => setChip(chip === "ready" ? "all" : "ready")} />
      </div>

      {/* Filters */}
      <div className="bg-surface border border-line rounded-md p-3 mb-5 flex flex-wrap items-center gap-2">
        <div className="flex flex-wrap gap-1.5">
          {CHIPS.map((c) => (
            <button key={c.key} data-testid={`queue-chip-${c.key}`} onClick={() => setChip(c.key)}
              className={`h-8 px-3 rounded-sm text-[12.5px] font-medium border transition-colors ${chip === c.key ? "bg-brand text-white border-brand" : "bg-surface text-ink-2 border-line hover:border-steel"}`}>{c.label}</button>
          ))}
        </div>
        <div className="h-6 w-px bg-line mx-1 hidden sm:block" />
        <Select value={customer} onValueChange={setCustomer}>
          <SelectTrigger data-testid="queue-filter-customer" className="h-8 border-line rounded-sm bg-surface text-[12.5px] w-auto min-w-[130px]"><SelectValue placeholder="Customer" /></SelectTrigger>
          <SelectContent><SelectItem value="all">Customer: All</SelectItem>{customers.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
        </Select>
        <Select value={stage} onValueChange={setStage}>
          <SelectTrigger data-testid="queue-filter-stage" className="h-8 border-line rounded-sm bg-surface text-[12.5px] w-auto min-w-[120px]"><SelectValue placeholder="Stage" /></SelectTrigger>
          <SelectContent><SelectItem value="all">Stage: All</SelectItem>{stages.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
        </Select>
        <span className="ml-auto text-[12.5px] text-ink-3 font-mono-data">{filtered.length} assigned</span>
      </div>

      {/* Sections */}
      {base.length === 0 ? (
        <div className="bg-surface border border-line rounded-md p-12 text-center text-sm text-ink-3">No active jobs assigned to {person === user?.name ? "you" : person} right now.</div>
      ) : (
        <div className="space-y-5">
          {SECTIONS.map((sec) => {
            const list = filtered.filter(sec.test);
            return (
              <div key={sec.key} data-section={sec.key} data-testid={`queue-section-${sec.key}`} className="bg-surface border border-line rounded-md overflow-hidden">
                <div className="px-4 py-3 border-b border-line flex items-center gap-2.5">
                  <h3 className="font-heading text-[14.5px] font-semibold text-ink">{sec.title}</h3>
                  <span className="font-mono-data text-[11px] text-ink-3 bg-surface-alt border border-line rounded-sm px-1.5 py-0.5">{list.length}</span>
                </div>
                {list.length === 0 ? (
                  <div className="px-4 py-6 text-center text-[12.5px] text-ink-3">Nothing here.</div>
                ) : (
                  <div>{list.map((j) => <QueueRow key={j.id} j={j} sectionKey={sec.key} />)}</div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </Layout>
  );
}
